import PrintOutlined from '@mui/icons-material/PrintOutlined';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FileDisplayBase } from 'library/components/controls/files/FileDisplayBase';
import { getFileType } from 'library/components/controls/files/functions';

import { printBlob } from './printBlob';

export interface PrintableAttachment {
	id: number;
	fileName: string;
}

interface PrintableAttachmentItemProps<T extends PrintableAttachment> {
	attachment: T;
	getFileUrl: (attachment: T) => string;
	onDownload: (attachment: T) => Promise<Blob>;
	printFailedMessage: string;
}

function PrintableAttachmentItem<T extends PrintableAttachment>({
	attachment,
	getFileUrl,
	onDownload,
	printFailedMessage,
}: PrintableAttachmentItemProps<T>) {
	const { t } = useTranslation(['commonButton']);
	const { enqueueSnackbar } = useSnackbar();
	const [isPrinting, setIsPrinting] = useState(false);

	const onPrint = async () => {
		setIsPrinting(true);
		try {
			const blob = await onDownload(attachment);
			printBlob(blob);
		} catch {
			enqueueSnackbar(printFailedMessage, { variant: 'error' });
		} finally {
			setIsPrinting(false);
		}
	};

	return (
		<FileDisplayBase
			name={attachment.fileName}
			url={getFileUrl(attachment)}
			type={getFileType(attachment.fileName)}>
			<Button
				variant="outlined"
				color="secondary"
				size="small"
				disableElevation
				loading={isPrinting}
				startIcon={<PrintOutlined />}
				onClick={onPrint}
				sx={{ marginLeft: 'auto' }}>
				{t('commonButton:print')}
			</Button>
		</FileDisplayBase>
	);
}

export interface PrintableAttachmentsSectionProps<
	T extends PrintableAttachment,
> {
	attachments: T[];
	getFileUrl: (attachment: T) => string;
	onDownload: (attachment: T) => Promise<Blob>;
	sectionTitle: string;
	printFailedMessage: string;
}

export function PrintableAttachmentsSection<T extends PrintableAttachment>({
	attachments,
	getFileUrl,
	onDownload,
	sectionTitle,
	printFailedMessage,
}: PrintableAttachmentsSectionProps<T>) {
	if (attachments.length === 0) {
		return null;
	}

	return (
		<Stack direction="column" spacing={1} sx={{ px: 3, pb: 3 }}>
			<Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
				{sectionTitle}
			</Typography>
			{attachments.map((attachment) => (
				<PrintableAttachmentItem
					key={attachment.id}
					attachment={attachment}
					getFileUrl={getFileUrl}
					onDownload={onDownload}
					printFailedMessage={printFailedMessage}
				/>
			))}
		</Stack>
	);
}
